<script lang="ts" context="module">
  import { orgRightsNrk, orgRightsOther, orgRightsUnknown } from "@nrk/origo";
  import { digitalSourceTypeFaksimile, type RightsMarker } from "bildebanken-model";

  type Option = {
    value: RightsMarker;
    header: string;
    info: string;
    icon: string;
    css: string;
  };

  const allOptions: Readonly<Option[]> = [
    {
      value: "Free",
      header: "Fri bruk",
      info: "Innhold kan gjenbrukes",
      icon: orgRightsNrk,
      css: "nrk org-button--radio-large",
    },
    {
      value: "Restricted",
      header: "Begrenset",
      info: "Innhold med bruksvilkår",
      icon: orgRightsOther,
      css: "restricted org-button--radio-large",
    },
    {
      value: "Unknown",
      header: "Ukjent",
      info: "Innhold mangler informasjon",
      icon: orgRightsUnknown,
      css: "unknown org-button--radio-large",
    },
  ];
</script>

<script lang="ts">
  import { type Image } from "bildebanken-model";
  import type { EditImageActor } from "./editImageMachine";
  import { updateField } from "./editImageMachine";
  import EditPhotographer from "./EditPhotographer.svelte";
  import InfoBox from "./InfoBox.svelte";
  import EditRightsHolder from "./EditRightsHolder.svelte";
  import VisibleForEndUsers from "./VisibleForEndUsers.svelte";
  import RightsIndicator from "../Common/RightsIndicator.svelte";
  import { onMount } from "svelte";
  import EditUsageTerms from "./EditUsageTerms.svelte";
  import { pageState } from "../../state/page";

  export let service: EditImageActor;
  export let image: Image;
  const options =
    $pageState.type === "UPLOAD" ? allOptions.filter((opt) => opt.value !== "Unknown") : allOptions;

  let imageInstructionsAltered = false;
  let overrideCreditEnabled = !!image.metadata?.rights?.credit;
  let instructionsValue: string | undefined;
  let creditLine: string | undefined;

  $: rightsholderRequired = rightsMarker === "Free";
  $: usageTermsRequired = rightsMarker === "Restricted";
  $: rightsMarker = image.metadata.rightsMarker || "Unknown";

  $: if (!imageInstructionsAltered) {
    instructionsValue = image.metadata.instructions ?? undefined;
  }

  function setRightsMarker(value?: RightsMarker) {
    if (value === "Unknown" || value === "Free") {
      updateField(service, "rights", {
        ...image.metadata.rights,
        note: undefined,
      });
    }
    updateField(service, "rightsMarker", value);
  }

  function handleCreditToggle() {
    overrideCreditEnabled = !overrideCreditEnabled;
    if (!overrideCreditEnabled) {
      const newRights = {
        ...image.metadata.rights,
        credit: null,
      };
      creditLine = undefined;
      updateField(service, "rights", newRights);
    }
  }

  function onChangeRightsMarker(this: HTMLInputElement & { value: RightsMarker }) {
    setRightsMarker(this.value);
  }

  function onChangeSource(this: HTMLInputElement) {
    updateField(service, "source", this.value);
  }

  function onChangeCredit(this: HTMLInputElement) {
    const newRights = {
      ...image.metadata.rights,
      credit: this.value || undefined,
    };
    creditLine = this.value || undefined;
    updateField(service, "rights", newRights);
  }

  function onChangeInstructions(this: HTMLInputElement) {
    imageInstructionsAltered = true;
    instructionsValue = this.value;
    if (instructionsValue) {
      setRightsMarker("Restricted");
    }
    updateField(service, "instructions", this.value);
  }

  onMount(() => {
    creditLine = image.metadata.rights?.credit || "";
  });
</script>

<div>
  <fieldset class="org-fieldset">
    {#each options as option (option.value)}
      <label
        class:selected={option.value === rightsMarker}
        class={`org-button org-button--radio-large ${
          options.length === 2 ? "upload-large" : "edit-large"
        }`}
      >
        <input
          data-testid={option.css}
          name="rights"
          type="radio"
          value={option.value}
          bind:group={rightsMarker}
          disabled={option.value === "Free" && !!instructionsValue}
          on:change={onChangeRightsMarker}
        />
        <span class="text">
          <span class="header"
            >{option.header}
            <RightsIndicator rightsMarker={option.value} /></span
          >
          <span class="info">{option.info}</span>
        </span>
      </label>
    {/each}
  </fieldset>
  <div style="margin-top: 1rem">
    <div class="photographer">
      <EditPhotographer {image} {service} />
    </div>
    <div>
      <EditRightsHolder {rightsMarker} {service} {image} {rightsholderRequired} />
      {#if image.metadata.digitalSourceType === digitalSourceTypeFaksimile}
        <div style="margin-top: 1rem">
          <label for="source"> Kilde for Faksimile </label>
          <input
            id="source"
            class="org-input"
            type="text"
            value={image.metadata.source ?? ""}
            on:input={onChangeSource}
            placeholder="Eksempler: URL, publikasjon med sidetall"
          />
        </div>
      {/if}
      <div style="margin-top: 1.25rem; font-weight: 700">
        <input
          type="checkbox"
          class="org-switch"
          data-testid="credit-override-toggle"
          on:change={handleCreditToggle}
          bind:checked={overrideCreditEnabled}
        />
        Overstyr kreditering
        <VisibleForEndUsers />
        <InfoBox
          header="Når skal kreditering overstyres?"
          link="https://portalen.nrk.no/fotografering-filming-og-publisering-av-bilder-og-video-juridisk/?/llqdiau0dp2"
        >
          <p class="content">
            Bruk dette feltet kun hvis fotograf- og rettighetshaver-feltene ikke skal vises ut til
            publikum. Her kan du legge inn en alternativ kreditering som erstatter standardfeltene.
          </p>
        </InfoBox>
        {#if overrideCreditEnabled}
          <input
            id="credit"
            class="org-input"
            style="margin-top: 0.25rem"
            type="text"
            data-testid="credit-override-input"
            value={creditLine ?? ""}
            on:input={onChangeCredit}
          />
        {/if}
      </div>
      {#if rightsMarker === "Restricted"}
        <EditUsageTerms {service} {image} {usageTermsRequired} {rightsMarker} />
      {/if}
      {#if (image.metadata.instructions || imageInstructionsAltered) && rightsMarker === "Restricted"}
        <label data-testid="Instruksjoner">
          Instruksjoner
          <span class={rightsMarker === "Restricted" && image.metadata.instructions ? "" : "hidden"}
            >*</span
          >
          <textarea
            data-testid="instruksjoner-input"
            class="org-input"
            rows="2"
            value={image.metadata.instructions ?? ""}
            on:input={onChangeInstructions}
            placeholder="Eksempler: sladd ansikt før publisering, kommer en ny versjon senere"
          />
        </label>
      {/if}
    </div>
  </div>
</div>

<style>
  .content {
    font-size: 12px;
    margin-top: 6px;
    margin-bottom: 6px;
  }

  .info {
    font-weight: normal;
  }

  .icon {
    display: flex;
    align-items: center;
    padding: var(--org-xsmall);
    border-radius: 6px;
  }

  .header {
    font-weight: 600;
    font-size: var(--org-font-2medium);
  }

  .text span {
    display: block;
    font-size: 14px;
  }

  .icon {
    display: flex;
    align-items: center;
    padding: var(--org-xsmall);
    color: var(--org-color-white);
    border-radius: 6px;
  }

  .hidden {
    display: none;
  }

  .edit-large {
    width: 100%;
  }

  .upload-large {
    min-width: 49%;
  }
</style>
